<template>
  <v-container>
    <p class="step-title"> {{ $t('insurancesStep.title') }} </p>
    <HfrCard v-if="userInfo && Object.keys(userInfo).length > 0">
      <div class="field-warning mb-0">{{ $t('global.prefilledDataWarning') }} </div>
    </HfrCard>
    <HfrCard>
      <v-form ref="assurancesForm">
        <v-text-field
          v-model="form.name"
          :label="$t('insurancesStep.name')"
          filled
          readonly
          class="required"
          :rules="[rules.required]"></v-text-field>
        <v-text-field
          v-model="form.avs_no"
          :label="$t('identifierStep.avsNo')"
          filled
          readonly
          class="required"
          :rules="[rules.required, rules.avs]"></v-text-field>

        <p class="additional-insurances-title ml-1">
          {{ $t('insurancesStep.otherInsurances') }}
        </p>

        <v-btn
          v-if="form.additionalInsurance === null"
          @click="addAdditionalInsurance"
          color="black"
          class="btn-multiline white--text pa-8 my-3"
          width="100%">
          <v-icon class="mr-1">mdi-plus</v-icon>
          <span class="text-wrap">
            {{ $t('insurancesStep.addInsCompl') }}
          </span>
        </v-btn>
        <ComplementaryInsuranceForm
          v-if="form.additionalInsurance !== null"
          :insurance="form.additionalInsurance"
          @input="updateAdditionalInsurance($event)"
          @removeInsurance="removeAdditionalInsurance">
        </ComplementaryInsuranceForm>
        <v-btn
          v-if="form.accidentInsurance === null"
          @click="addAccidentInsurance"
          color="black"
          class="btn-multiline white--text pa-8 my-3"
          width="100%">
          <v-icon class="mr-1">mdi-plus</v-icon>
          <span class="text-wrap">
            {{ $t('insurancesStep.addInsAccident') }}
          </span>
        </v-btn>
        <AccidentInsuranceForm
          v-if="form.accidentInsurance !== null"
          :insurance="form.accidentInsurance"
          @input="updateAccidentInsurance($event)"
          @removeInsurance="removeAccidentInsurance">
        </AccidentInsuranceForm>
        <v-btn
          v-if="form.aiInsurance === null"
          @click="addAiInsurance"
          color="black"
          class="btn-multiline white--text pa-8 my-3"
          width="100%">
          <v-icon class="mr-1">mdi-plus</v-icon>
          <span class="text-wrap"> {{ $t('insurancesStep.addInsAI') }} </span>
        </v-btn>
        <AdditionalInsuranceForm
          v-if="form.aiInsurance !== null"
          :insuranceType="$t('insurancesStep.insuranceAI')"
          :insurance="form.aiInsurance"
          :readonly=true
          @input="updateAiInsurance($event)"
          @removeInsurance="removeAiInsurance"></AdditionalInsuranceForm>

        <v-btn
          v-if="form.militaryInsurance === null"
          @click="addMilitaryInsurance"
          color="black"
          class="btn-multiline white--text pa-8 my-3"
          width="100%">
          <v-icon class="mr-1">mdi-plus</v-icon>
          <span class="text-wrap"
            >{{ $t('insurancesStep.addInsMilitary') }}
          </span>
        </v-btn>
        <AdditionalInsuranceForm
          v-if="form.militaryInsurance !== null"
          :insuranceType="$t('insurancesStep.insuranceMil')"
          :insurance="form.militaryInsurance"
          :readonly=true
          @input="updateMilitaryInsurance($event)"
          @removeInsurance="removeMilitaryInsurance"></AdditionalInsuranceForm>
      </v-form>
    </HfrCard>
    <v-alert
      class="mt-2"
      v-model="displayNotification"
      color="red"
      dismissible
      elevation="2"
      type="error">
      {{ notification }}
    </v-alert>
  </v-container>
</template>
<script>
import HfrCard from '@/components/ui/HfrCard';
import AdditionalInsuranceForm from '@/components/ui/AdditionalInsuranceForm';
import { mapActions, mapMutations, mapState } from 'vuex';
import { avsNoRule, requiredRule } from '@/helpers/validationRules';
import AccidentInsuranceForm from "@/components/ui/AccidentInsuranceForm.vue";
import ComplementaryInsuranceForm from "@/components/ui/ComplementaryInsuranceForm.vue";
export default {
  components: { HfrCard, AdditionalInsuranceForm, AccidentInsuranceForm, ComplementaryInsuranceForm },
  data() {
    return {
      stepNumber: 8,
      form: {
        name: process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'Rock Assurance' : null,
        avs_no: process.env.VUE_APP_PREFILL_FORMS === 'true' ? 7560000000666 : null,
        additionalInsurance: null,
        accidentInsurance: null,
        aiInsurance: null,
        militaryInsurance: null
      },
      rules: {
        required: requiredRule,
        avs: avsNoRule
      },
      displayNotification: false,
      notification: null,
      processing: false
    };
  },
  watch: {
    navigateToNextStep: {
      handler(navigateToNextStep) {
        if (this.currentStep === this.stepNumber && navigateToNextStep && !this.processing) {
          this.validateAssurancesForm();
        }
      }
    },
    userInfo: {
      handler() {
        this.fillFormDataWithUserInfo();
      }
    },
    insuranceCardInfo: {
      handler(insuranceCardInfo) {
        if (insuranceCardInfo) {
          this.fillFormData();
        }
      }
    }
  },
  methods: {
    ...mapActions('navigation', ['doNextStep', 'toggleNavigateToNextStep']),
    ...mapActions('preAdmission', [
      'sendPreAdmissionData',
      'storeAssurancesStep'
    ]),
    ...mapMutations('preAdmission', ['storePreAdmissionSuccess']),
    validateAssurancesForm() {
      if (this.$refs.assurancesForm.validate()) {
        this.storeAssurancesStep(this.form);
        this.processing = true;
        this.sendPreAdmissionData()
          .then(() => this.showConfirmation())
          .catch(() => {
            this.notification = this.$t('notifications.general.error');
            this.displayNotification = true;
            this.processing = false;
          });
      }

      this.toggleNavigateToNextStep();
    },
    fillFormDataWithUserInfo() {
      this.form.aiInsurance = null;
      this.form.militaryInsurance = null;
      const insurances = this.userInfo?.patient?.insurances || []
      const assuranceCompl = insurances.find((a) => a.type === 'C');
      const assuranceAccident = insurances.find((a) => a.type === 'A');

      if (assuranceCompl) {
        this.form.additionalInsurance = {
          name: assuranceCompl.name,
          insuranceCardNumber: assuranceCompl.complementary_card_no,
          roomDivision: assuranceCompl.room_division
        }
      } else {
        this.form.additionalInsurance = null;
      }

      if (assuranceAccident) {
        this.form.accidentInsurance = {
          name: assuranceAccident.name,
          roomDivision: assuranceAccident.room_division
        }
      } else {
        this.form.accidentInsurance = null;
      }
      if (this.userInfo && this.userInfo.patient && this.userInfo.patient.avs_no) {
        this.form.avs_no = this.userInfo.patient.avs_no
      } else {
        this.fillFormData()
      }
      this.$refs.assurancesForm.resetValidation();
    },



    fillFormData() {
      const infos = this.insuranceCardInfo?.data;
      if (infos != null) {
        const baseInformation = infos['CH-Baseinformation'];
        this.form.name =
          baseInformation.administrativeData.nameOfTheInstitution;
        this.form.avs_no =
          baseInformation.identificationData.cardholderIdentifier;
      }
    },
    addAdditionalInsurance() {
      this.form.additionalInsurance = {};
    },
    updateAdditionalInsurance(value) {
      this.form.additionalInsurance = value;
    },
    removeAdditionalInsurance() {
      this.form.additionalInsurance = null;
    },
    addAccidentInsurance() {
      this.form.accidentInsurance = {};
    },
    updateAccidentInsurance(value) {
      this.form.accidentInsurance = value;
    },
    removeAccidentInsurance() {
      this.form.accidentInsurance = null;
    },
    addAiInsurance() {
      this.form.aiInsurance = { name: this.$t('insurancesStep.insuranceAI')};
    },
    updateAiInsurance(value) {
      this.form.aiInsurance = value;
    },
    removeAiInsurance() {
      this.form.aiInsurance = null;
    },
    addMilitaryInsurance() {
      this.form.militaryInsurance = { name: this.$t('insurancesStep.insuranceMil')};
    },
    updateMilitaryInsurance(value) {
      this.form.militaryInsurance = value;
    },
    removeMilitaryInsurance() {
      this.form.militaryInsurance = null;
    },
    showConfirmation() {
      this.storePreAdmissionSuccess(true);
    }
  },
  computed: {
    ...mapState('preAdmission', ['userInfo']),
    ...mapState('navigation', ['navigateToNextStep', 'currentStep']),
    ...mapState('insuranceCard', ['insuranceCardInfo'])
  }
};
</script>
<style lang="scss">
.additional-insurances-title {
  font-weight: bold;
}
</style>
